import type {WebWorker} from '../utilities/web-worker'

export class DebouncedWorkerManager<TRequest, TResponse> {
  #currentRequest: TRequest | undefined = undefined
  #nextRequest: TRequest | undefined = undefined
  onResponse: ((response: TResponse) => void) | undefined
  #delayId: NodeJS.Timeout | undefined
  #worker: Worker | WebWorker<TRequest, TResponse>
  #delayMs: number
  #debounceOverrideCondition: ((req: TRequest) => boolean) | undefined

  constructor(
    worker: Worker | WebWorker<TRequest, TResponse>,
    delayMs = 200,
    debounceOverrideCondition?: (req: TRequest) => boolean,
  ) {
    this.#worker = worker
    this.#delayMs = delayMs
    this.#debounceOverrideCondition = debounceOverrideCondition
    this.#worker.onmessage = ({data}: {data: TResponse}) => {
      if (this.onResponse) {
        this.onResponse(data)
      }
      if (this.#nextRequest) {
        this.#postNow(this.#nextRequest)
        this.#nextRequest = undefined
      } else {
        this.#currentRequest = undefined
      }
    }
  }

  post(request: TRequest) {
    if (this.#debounceOverrideCondition && this.#debounceOverrideCondition(request)) {
      if (this.#delayId) {
        clearTimeout(this.#delayId)
      }
      return this.#postNow(request)
    }

    if (this.idle()) {
      if (this.#delayId) {
        clearTimeout(this.#delayId)
      }

      this.#delayId = setTimeout(() => {
        this.#postNow(request)
      }, this.#delayMs)
    } else {
      this.#nextRequest = request
    }
  }

  #postNow(request: TRequest) {
    this.#currentRequest = request
    this.#worker.postMessage(request)
  }

  idle() {
    return !this.#currentRequest
  }

  terminate() {
    this.#worker.terminate()
  }
}
